import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';
import { Map, List } from 'immutable';
import classnames from 'classnames';

import Popup from '../helpers/Popup';
import FetchingError from '../helpers/FetchingError';
import FormErrors from '../helpers/FormErrors';
import { InlineInput } from '../helpers/Input';
import FormGroup from '../helpers/FormGroup';
import Checkbox from '../helpers/Checkbox';
import Textarea from '../helpers/Textarea';
import ActivityDropdown from '../helpers/ActivityDropdown';
import Button from '../helpers/Button';
import AutoComplete from '../helpers/AutoComplete';
import config from '../../config';
import Select from '../helpers/Select';

const SetNextActionsPopup = props => {
  const {
    activities,
    onUpdate,
    onClose,
    onChange,
    onActivityChange,
    onStatusChange,
    onDateChange,
    onDelete,
    assignedToSuggestions,
    formData,
    formError,
    statuses,
    isPopupFetching,
    isPopupFetchingError,
    isSubmitting,
    descriptionHidden,
    backlogHidden,
    applyToHidden,
    user,
  } = props;
  const [isDisabledDelete, setDisabledDelete] = useState(false);

  const header = 'Set Next Actions';
  const disableUpdate =
    !formData.get('date') ||
    !formData.getIn(['activity', 'text']) ||
    !formData.getIn(['activity', 'selected']) ||
    !formData.getIn(['assignedTo', 'selected']);
  const updateAction = isDisabledDelete ? onDelete : onUpdate;
  const footer = (
    <>
      <Button
        key="addTag"
        className="btn-primary"
        disabled={disableUpdate && !isDisabledDelete}
        loading={isSubmitting}
        onClick={updateAction}
      >
        Update
      </Button>
      <Button key="close" onClick={onClose}>
        Close
      </Button>
    </>
  );
  const applyToClass = classnames({
    hide: applyToHidden,
  });

  const backlogClass = classnames({
    hide: backlogHidden,
  });

  const descriptionClass = classnames({
    row: true,
    hide: descriptionHidden,
  });

  const userRole = user.getIn(['roles', 0, 'slug']);
  const isAvailableDeleting = [config.DIRECTOR, config.ADMINISTRATOR].includes(userRole);

  const handleDeleteOutstandingChange = event => {
    setDisabledDelete(event.target.checked);
  };

  return (
    <Popup footer={footer} header={header} id="setNextActionPopup" loading={isPopupFetching}>
      <p>Schedule the following Next Action for the selected companies:</p>
      <div className="row">
        <FormGroup className="col-xs-4">
          <InlineInput
            disabled={isDisabledDelete}
            label="Date"
            name="date"
            onChange={onDateChange}
            placeholder="date"
            type="date"
            value={formData.get('date')}
            autoFocus
            showLabel
          />
        </FormGroup>
        <AutoComplete
          {...assignedToSuggestions}
          formGroupClass="col-xs-4"
          inputProps={{
            ...assignedToSuggestions.inputProps,
            disabled: isDisabledDelete,
          }}
        />
        <FormGroup className="col-xs-4">
          <Select
            disabled={isDisabledDelete}
            id="set-next-actions-status"
            label="New Status"
            name="nextActionStatus"
            onChange={onStatusChange}
            options={statuses}
            value={formData.getIn(['status', 'selected']) || ''}
            showLabel
          />
        </FormGroup>
      </div>
      <div className="row">
        <FormGroup className="col-xs-4">
          <ActivityDropdown
            activities={activities}
            disabled={isDisabledDelete}
            info={formData.get('activity', Map())}
            onChange={onActivityChange}
          />
        </FormGroup>
      </div>
      <div className={descriptionClass}>
        <FormGroup className="col-xs-12">
          <Textarea
            disabled={isDisabledDelete}
            label="Description"
            name="description"
            onChange={onChange}
            placeholder="description"
            rows="3"
            value={formData.get('description')}
            showLabel
          />
        </FormGroup>
      </div>
      <div className={applyToClass}>
        <p className="form-section-label">Apply to</p>
        <FormGroup className="group-checkbox">
          <Checkbox
            checked={formData.get('approved', false)}
            disabled={isDisabledDelete}
            label="Approved"
            name="approved"
            onChange={onChange}
          />
          <Checkbox
            checked={formData.get('passed', false)}
            disabled={isDisabledDelete}
            label="Excluded"
            name="passed"
            onChange={onChange}
          />
          <Checkbox
            checked={formData.get('noResponse', false)}
            disabled={isDisabledDelete}
            label="No Response"
            name="noResponse"
            onChange={onChange}
          />
        </FormGroup>
      </div>
      <p className="form-section-label">Options</p>
      <FormGroup className="group-checkbox">
        <div className={backlogClass}>
          <Checkbox
            checked={formData.get('backlog', false)}
            disabled={isDisabledDelete}
            label="Backlog immediately"
            name="backlog"
            onChange={onChange}
          />
        </div>
        <Checkbox
          capitalizeLabel={false}
          checked={formData.get('override', false)}
          disabled={isDisabledDelete}
          label="Override existing Next Actions of companies that have outstanding Next Actions"
          name="override"
          onChange={onChange}
        />
        {isAvailableDeleting && (
          <Checkbox
            capitalizeLabel={false}
            label="Delete outstanding Next Actions for selected"
            name="na_delete"
            onChange={handleDeleteOutstandingChange}
          />
        )}
      </FormGroup>
      <FetchingError error={isPopupFetchingError} />
      <FormErrors className="error text-danger" errors={formError} />
    </Popup>
  );
};

SetNextActionsPopup.propTypes = {
  applyToHidden: PropTypes.bool.isRequired,
  assignedToSuggestions: PropTypes.object.isRequired,
  backlogHidden: PropTypes.bool.isRequired,
  descriptionHidden: PropTypes.bool.isRequired,
  formData: PropTypes.instanceOf(Map).isRequired,
  isPopupFetching: PropTypes.bool,
  isPopupFetchingError: PropTypes.instanceOf(Map),
  isSubmitting: PropTypes.bool,
  onActivityChange: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  statuses: PropTypes.instanceOf(List).isRequired,
};

export default memo(SetNextActionsPopup);
